<template>
  <div>
    <v-alert v-if="noCoolerProducts && noFreezerProducts" color="danger" dark border="top" dismissible>
      {{ $t('no_products_contact_support') }}
    </v-alert>
    <dialog-confirm ref="generic-dialog-confirm">
      <template #title>
        {{ genericDialogTitle }}
      </template>
      <div>
        {{ genericDialogContent }}
      </div>
    </dialog-confirm>
    <dialog-confirm ref="vehicle-overlap-dialog">
      <template #title>
        {{ $t('route_overlap') }}
      </template>
      {{ $tc('route_overlap_details_preamble', conflictingRoutes.length, {vehicle_name: get(localRoute, 'vehicle.name')}) }}
      <v-list two-line>
        <v-list-item v-for="route in conflictingRoutes" :key="route.id">
          <v-list-item-icon class="ml-n3">
            <router-link :to="`/route/${route.id}`" target="_blank" class="d-flex align-center">
              {{ route.name }}<v-icon small color="primary" class="ml-1">mdi-open-in-new</v-icon>
            </router-link>
          </v-list-item-icon>
          <v-list-item-content>
            <div class="d-flex">
              {{ $t('start') }}:
              <multi-time-display :time="route.start" class="ml-4"/>
            </div>
            <div v-if="route.end" class="d-flex mt-2">
              {{ $t('end') }}:
              <multi-time-display :time="route.end" />
            </div>
            <div v-else>
              {{ $t('in_progress') }}
            </div>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      {{ $tc('route_overlap_details_closing', conflictingRoutes.length) }}
    </dialog-confirm>
    <v-card>
      <v-card-text>
        <v-form @submit.prevent="onSubmit" autocomplete="off" name="lastpass-disable-search">
          <v-row>
            <v-col>
              <v-row>
                <v-col>
                  <route-plan-picker
                    v-if="useRoutePlans"
                    v-model="localRoute.route_plan"
                    :customerId="localRoute.customer.id"
                    :error-messages="routePlanErrors"
                    @no-route-plans="disableRoutePlans"/>
                  <route-name-picker
                    v-else
                    v-model="localRoute.name"
                    :error-messages="routeNameErrors"
                    :routeNameRules="routeNameRules"/>
                  <vehicle-picker
                    v-model="localRoute.vehicle"
                    :customer="localRoute.customer"
                    :error-messages="vehicleErrors"/>
                </v-col>
                <v-col>
                  <route-times
                    ref="route-times"
                    :show-end="showEndTimes"
                    :start.sync="localRoute.start"
                    :end.sync="localRoute.end"
                    :editing-route="mode === 'update'"
                    :timezone="localRoute.customer.timezone()"/>
                </v-col>
              </v-row>
            </v-col>
            <v-col>
              <v-row>
                <v-col>
                  <route-product-selection
                    ref="cooler-route-products"
                    zone="cooler"
                    :populateWithDefaults="mode === 'create'"
                    :label="$tc('cooler_products')"
                    v-model="products.cooler"
                    :route-customer="localRoute.customer"
                    :read-only-start-temps="readOnlyStartTemps"
                    @no-products="noCoolerProducts = true"/>
                  <p v-if="!isEmpty(routeProductErrors)" class="mt-2 text-subtitle-2 danger--text">
                    {{ first(routeProductErrors) }}
                  </p>
                </v-col>
                <v-col>
                  <route-product-selection
                    ref="freezer-route-products"
                    zone="freezer"
                    :populateWithDefaults="mode === 'create'"
                    :label="$tc('freezer_products')"
                    v-model="products.freezer"
                    :route-customer="localRoute.customer"
                    :read-only-start-temps="readOnlyStartTemps"
                    @no-products="noFreezerProducts = true"/>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <div class="d-flex justify-space-between mt-4">
            <div class="pro-tip" v-html="$t('pro_tip_route_entry')"></div>
            <div>
              <span v-if="routeDuration" class="mr-4">
                <span v-if="routeDuration.asMilliseconds() <= 0" class="invalid-duration">
                  {{ $t('invalid_duration') }}
                </span>
                <span v-else class="route-duration">{{ $t('route_duration') }}: {{ routeDuration | humanizeDuration({ mostPreciseUnit: 'minutes' }) }}</span>
              </span>
              <v-btn
                v-if="mode === 'create'"
                type="submit"
                color="primary"
                @click="$gtag.event('create', {event_category: 'Route'})"
                :loading="isSaving">
                <v-icon class="ml-n2">mdi-plus</v-icon>{{ $t('create_route') }}
              </v-btn>
              <v-btn
                v-else
                type="submit"
                color="primary"
                @click="$gtag.event('update', {event_category: 'Route'})"
                :loading="isSaving"
                :disabled="localRoute.archived">
                <v-icon class="ml-n2">mdi-content-save</v-icon>{{ $t('update_route') }}
              </v-btn>
            </div>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import Vue from 'vue'
import DialogConfirm from '@/components/shared/DialogConfirm'
import RoutePlanPicker from '@/components/route/form/RoutePlanPicker'
import RouteNamePicker from '@/components/route/form/RouteNamePicker'
import VehiclePicker from '@/components/route/form/VehiclePicker'
import RouteTimes from '@/components/route/form/RouteTimes'
import RouteProductSelection from '@/components/route/form/RouteProductSelection'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay.vue'
import TemperatureConverterUtil from '@/util/TemperatureConverterUtil'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import { required } from 'vuelidate/lib/validators'
import { get, cloneDeep, remove, isEmpty, flatMap, each, first } from 'lodash'

export default {
  name: 'RouteForm',
  components: {
    DialogConfirm,
    RoutePlanPicker,
    RouteNamePicker,
    VehiclePicker,
    MultiTimeDisplay,
    RouteTimes,
    RouteProductSelection
  },
  mixins: [
    ApiUtilitiesMixin,
    DateTimeUtilsMixin,
    ValidationMixin,
    CurrentUserMixin
  ],
  props: {
    route: Object
  },
  validations: {
    localRoute: {
      vehicle: {
        required
      },
      route_plan: {
        required: function (value) {
          return this.useRoutePlans ? value != null : true
        }
      },
      name: {
        required: function (value) {
          return !this.useRoutePlans ? value != null : true
        },
        maxLength: function (value) {
          if (!this.useRoutePlans && value != null) {
            return value.length <= 30
          }
          return true
        }
      }
    },
    products: {
      atLeastOneProductValidation: function (value, vm) {
        return (!isEmpty(vm.products.cooler) || !isEmpty(vm.products.freezer))
      }
    }
  },
  data () {
    return {
      isSaving: false,
      useRoutePlans: false,
      routeNameRules: null,
      localRoute: null,
      noCoolerProducts: false,
      noFreezerProducts: false,
      products: {
        cooler: [],
        freezer: []
      },
      // The conflicting routes field used in the vehicle overlap modal.
      conflictingRoutes: [],
      // A function that holds the resolver for a promise created for the vehicle overlap modal.
      resolveVehicleOverlapModal: null,
      genericDialogTitle: null,
      genericDialogContent: null
    }
  },
  computed: {
    mode () {
      return this.localRoute.id ? 'update' : 'create'
    },
    showEndTimes () {
      // If updating a route, never hide end times. (VF-1534)
      if (this.mode === 'update') {
        return true
      }
      // If automatic route ending is on, then hide the end date pickers, but otherwise default to showing.
      const routeDetectionOpts = this.localRoute.customer.getSetting('processing.RouteDetection', false)

      if (routeDetectionOpts) {
        return routeDetectionOpts.enableRouteEnding !== 1
      }
      // default to true
      return true
    },
    routeDuration () {
      if (this.localRoute.start && this.localRoute.end) {
        return Moment.duration(Moment(this.localRoute.end).diff(Moment(this.localRoute.start)))
      }
      return undefined
    },
    vehicleErrors () {
      return this.validationErrors([{
        prop: this.$v.localRoute.vehicle,
        rule: 'required',
        message: this.$t('vehicle_selection_required')
      }])
    },
    routePlanErrors () {
      return this.validationErrors([{
        prop: this.$v.localRoute.route_plan,
        rule: 'required',
        message: this.$t('route_selection_required')
      }])
    },
    routeNameErrors () {
      return this.validationErrors([{
        prop: this.$v.localRoute.name,
        rule: 'required',
        message: this.$t('route_selection_required')
      }, {
        prop: this.$v.localRoute.name,
        rule: 'maxLength',
        message: this.$t('max_length_error', {
          entity: this.$t('route_name'),
          max: 30
        })
      }])
    },
    routeProductErrors () {
      return this.validationErrors([{
        prop: this.$v.products,
        rule: 'atLeastOneProductValidation',
        message: this.$t('add_at_least_one_product_to_route')
      }])
    },
    /**
     * A flag to determine if the start temps fields in the route product selectors should be disabled.
     * Admins and Root can always change it, but regular users have it disabled if the route has started.
     * @returns {boolean}
     */
    readOnlyStartTemps () {
      if (this.user.can('bypass_data_quality_route')) {
        return false
      }
      return this.mode === 'update' && Moment(this.route.start).isBefore(Moment())
    }
  },
  created () {
    this.initialize()
  },
  methods: {
    get: get,
    isEmpty: isEmpty,
    first: first,
    /**
     * Initialize the component.
     */
    initialize () {
      if (this.route) {
        this.localRoute = cloneDeep(this.route)
        this.localRoute.timezone = this.route.customer.timezone()
        // Convert start/end times to Moment (if present)
        if (this.route.start) {
          this.localRoute.start = Moment.tz(this.route.start, this.localRoute.timezone)
        }
        if (this.route.end) {
          this.localRoute.end = Moment.tz(this.route.end, this.localRoute.timezone)
        }
        this.products = this.route.zone_indexed_route_products
        // If the route product's temperature scale differs from the route customer's temperature scale, convert the
        // start temps. This shouldn't generally be necessary unless a customer has access to products whose primary
        // customer is on a different temperature scale, but you never know.
        const custScale = this.localRoute.customer.tempFormat()
        each(this.products.cooler, rp => {
          rp.start_temp = TemperatureConverterUtil.to(rp.start_temp, rp.temperature_scale, custScale)
          rp.temperature_scale = custScale
        })
        each(this.products.freezer, rp => {
          rp.start_temp = TemperatureConverterUtil.to(rp.start_temp, rp.temperature_scale, custScale)
          rp.temperature_scale = custScale
        })
      } else {
        // New route
        if (this.$route.params.planid) {
          this.$orm('RoutePlan')
            .find(this.$route.params.planid)
            .then((routePlan) => {
              this.localRoute = this.$orm('Route').createRecord({
                start: Moment.tz(routePlan.start, this.customerTimezone),
                end: Moment.tz(routePlan.stop, this.customerTimezone),
                timezone: this.customerTimezone,
                customer: this.customerContext,
                route_plan_id: routePlan.id,
                name: routePlan.route_number
              })
              this.products = {
                cooler: [],
                freezer: []
              }
            })
            .catch(error => {
              if (error) {
                this.renderErrorModal(error)

                // Re-throw the error so that it shows up in the js console and is
                // reported to our error handler end-point
                throw (error)
              }
            })
        } else {
          this.localRoute = this.$orm('Route').createRecord({
            start: null,
            end: null,
            timezone: this.customerTimezone,
            customer: this.customerContext
          })
          this.products = {
            cooler: [],
            freezer: []
          }
        }
      }
      this.useRoutePlans = this.localRoute.customer.getSetting('routePlanRouteCreate', false)
      this.routeNameRules = this.localRoute.customer.getSetting('routeNames')
    },
    /**
     * Reset the component back to initial state
     */
    reset () {
      this.$v.$reset()
      // Wait a tick for any changed values to propagate to child components
      Vue.nextTick(() => {
        this.$refs['cooler-route-products'].reset()
        this.$refs['freezer-route-products'].reset()
        this.$refs['route-times'].reset()
      })
    },
    /**
     * When route plans are enabled in the customer settings but the route plan picker can't find any route plans, fall
     * back to route name picker.
     *
     * Note:
     * This is how the page has always worked, but when re-implementing this page I had some thoughts as whether this is
     * always a good idea: for example, suppose there are no available route plans and a user goes to edit an older
     * route - this would mean that the picker would switch to the route names picker. Then they would be able to edit
     * the route name and unlink the route plan.
     * But this might be fine, this should never happen except accidentally, I think.
     */
    disableRoutePlans () {
      this.useRoutePlans = false
    },
    /**
     * Wrapper to the generic dialog so we can reuse it in a few contexts.
     * @param title
     * @param content
     * @returns {Promise<*>}
     */
    genericDialogConfirm (title, content) {
      this.genericDialogTitle = title
      this.genericDialogContent = content
      return this.$refs['generic-dialog-confirm'].confirm()
    },
    /**
     * Before creating the Route, warn the user if the route is over 30 days in the past.
     */
    async checkValidDate () {
      const thirtyDaysAgo = Moment().subtract(30, 'days')
      if (this.localRoute.start.isBefore(thirtyDaysAgo)) {
        if (!await this.genericDialogConfirm(this.$tc('old_route_date'), this.$tc('old_route_date_message'))) {
          return false
        }
      }
      return true
    },
    /**
     * Before submitting the Route, check to see if a route with the same name has been created in the last day. If so,
     * show a warning to that effect and allow the user to confirm that the save process should continue.
     *
     * @returns Boolean Whether the process should continue
     */
    async checkDuplicateRoute () {
      const response = await this.$orm('Route').findAll({
        customer_id: this.localRoute.customer.id,
        name: this.localRoute.name,
        created_after: '-1 day',
        sort: '-created'
      })
      const duplicateRoutes = response.getItems()
      // If there are duplicate route(s), display the most recent and prompt the user to confirm continuation.
      if (duplicateRoutes.length > 0) {
        const duration = Moment.duration(Moment(duplicateRoutes[0].created).diff(Moment()))
        const duplicateModalMessage = this.$tc('duplicate_route_override', {
          name: this.localRoute.name,
          duration: duration.humanize(true)
        })
        if (!await this.genericDialogConfirm(this.$tc('duplicate_route'), duplicateModalMessage)) {
          return false
        }
      }
      return true
    },

    /**
     * Before submitting the route, check to see if the selected vehicle is already on a route or has a route planned
     * that might conflict with this route. If so, show a warning to that effect and allow the user to confirm
     * that the save process should continue.
     *
     * @returns Boolean Whether the process should continue
     */
    async checkVehicleOverlap () {
      // We need to find routes under two conditions:
      //   1) Routes which start before this route ends (if an end is set) and end after our this route starts.
      //   2) Routes which start before this route ends (if an end is set) and which have no end.
      //
      // Both of these cases are considered conflicting, in the case of routes with no end set they may resolve with no
      // conflict in teh future (when the end is finally determined but we have no way of knowing at this point in time)

      // Case 1:
      const conflictingEndedRouteParameters = {
        vehicle_id: this.localRoute.vehicle.id,
        customer_id: this.localRoute.customer.id,
        end_after: this.localRoute.start.format()
      }

      // Case 2:
      const conflictingUnendedRouteParameters = {
        vehicle_id: this.localRoute.vehicle.id,
        customer_id: this.localRoute.customer.id,
        end_not_set: true
      }

      // In both cases if a route end is set then limit the results that start before this on ends
      if (this.localRoute.end) {
        conflictingEndedRouteParameters.start_before = Moment(this.localRoute.end).format()
        conflictingUnendedRouteParameters.start_before = Moment(this.localRoute.end).format()
      }

      const conflictingEndedRoutesPromise = this.$orm('Route').findAll(conflictingEndedRouteParameters)
      const conflictingUnendedRoutesPromise = this.$orm('Route').findAll(conflictingUnendedRouteParameters)

      // Make both calls and show the modal if any conflicting routes are found
      const results = await Promise.all([conflictingEndedRoutesPromise, conflictingUnendedRoutesPromise])
      const conflictingRoutes = flatMap(results, x => x.getItems())

      // Filter out the current route (in the case of updating an existing route)
      remove(conflictingRoutes, x => x.id === this.localRoute.id)

      if (conflictingRoutes.length > 0) {
        this.conflictingRoutes = conflictingRoutes
        return await this.$refs['vehicle-overlap-dialog'].confirm()
      }
      return true
    },
    /**
     * Perform the route save via the API. On success, emit an event to signify
     * that the Route was successfully saved.
     */
    saveRoute () {
      // This record creation must include route_products in the message body.
      // However, the JS-Data api adapter (VeroFreshApiAdapter) doesn't really support including the bodies of
      // relations, only any associated FKs.

      // Normally it tries to strip any relations out of the message body. It's possible this may work via the 'with'
      // option, but sometimes will also try to create/update the entities of relations automatically, which we may not
      // want (and we don't fully understand implications of how that might work, yet.)

      // So instead of using the api adapter, we will manually create the JSON, convert it to a string, and send via the
      // http adapter (VeroFreshHttpAdapter)
      const json = this.localRoute.toJSON({ with: ['route_products'] })

      // Transform the start and end times from moment object to formatted strings which the API can ingest
      json.start = this.localRoute.start.format()
      // Always send `end` explicitly. If the user cleared the end date we must send `null` so the
      // API clears it; omitting the key leaves the stored value untouched (Yii only assigns keys
      // present in the payload), which silently drops the removal and produces no audit entry (VF-4131).
      json.end = this.localRoute.end ? this.localRoute.end.format() : null

      if (this.localRoute.id) {
        return this.$httpAdapter({
          url: this.$orm('Route').endpoint + '/' + this.localRoute.id,
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify(json)
        })
      } else {
        return this.$httpAdapter({
          url: this.$orm('Route').endpoint,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          data: JSON.stringify(json)
        })
      }
    },
    /**
     * Submit the form and save the Route.
     */
    async onSubmit () {
      await this.$store.dispatch('globalMessagingModule/clearFlashBanners')
      const valid = this.validate()
      if (!valid) {
        await this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', this.$t('route_form_validation_failed'))
        return false
      }
      // Prepare the selected route products to be submitted as JSON
      this.localRoute.route_products = this.products.cooler.concat(this.products.freezer)
      // If we're using route plans, use the route plan's name as the route's name.
      if (this.useRoutePlans) {
        this.localRoute.name = this.localRoute.route_plan.route_number
      }
      this.isSaving = true
      try {
        if (this.mode === 'create' && !await this.checkValidDate()) {
          return false
        }
        if (this.mode === 'create' && !await this.checkDuplicateRoute()) {
          return false
        }
        if (!await this.checkVehicleOverlap()) {
          return false
        }
        const result = await this.saveRoute()
        const route = this.$orm('Route').createRecord(result.data)

        if (this.mode === 'create') {
          this.$emit('created', route)
        } else {
          this.$emit('updated', route)
        }
      } catch (error) {
        this.renderRecordSaveErrorFlashBanner(error)
        throw (error)
      } finally {
        this.isSaving = false
      }
    },
    /**
     * Validate the form (and all subcomponents of the form)
     *
     * @returns Boolean If the form is valid.
     */
    validate () {
      this.$v.$touch()
      const valid = !this.$v.$invalid
      const routeTimesValid = this.$refs['route-times'].validate()
      const coolerRouteProductsValid = this.$refs['cooler-route-products'].validate()
      const freezerRouteProductsValid = this.$refs['freezer-route-products'].validate()
      return valid && routeTimesValid && coolerRouteProductsValid && freezerRouteProductsValid
    }
  }
}
</script>

<style lang="scss" scoped>
.pro-tip {
  font-style: italic;
}
.pro-tip ::v-deep(.callout-key) {
  border: 1px solid #cccccc;
  border-radius: 3px;
  display: inline-block;
  font-style: normal;
  padding-left: 5px;
  padding-right: 3px;
  margin-left: 3px;
  margin-right: 3px;
}
</style>
